import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import axios from 'axios';
import AdyenCheckout from '@adyen/adyen-web';
import '@adyen/adyen-web/dist/adyen.css';
import WaitingBlock from './WaitingBlock';

const Adyen = React.forwardRef(({ config, token, lyriaUrl, hidePayButton, onPaymentStarted, onPaymentResult, loader, savePaymentMethod }, ref) => {
    const [currentView, setCurrentView] = React.useState('adyen');
    const container = React.useRef();
    const [attempts, setAttempts] = React.useState(0);
    const mustRecreateDropin = React.useRef(true);
    const iframe = React.useRef();
    const popup = React.useRef();
    const dropin = React.useRef();

    const handleSuccess = React.useCallback(() => {
        onPaymentResult({ success: true });
    }, [onPaymentResult]);

    const handleError = React.useCallback((response) => {
        mustRecreateDropin.current = true;
        setAttempts(attempts => attempts + 1);
        setCurrentView('adyen');
        onPaymentResult({ success: false, data: response });
    }, [onPaymentResult, setAttempts, setCurrentView]);

    const postMessageHandler = message => {
        if (message.origin === lyriaUrl) {
            if (message.data.status === 'success') {
                handleSuccess();
            } else if (message.data.status === 'error') {
                handleError(message.data.data);
            }
        }
    };

    React.useEffect(() => {
        window.addEventListener('message', postMessageHandler);
        return () => window.removeEventListener('message', postMessageHandler);
    });

    React.useEffect(() => {
        const element = container.current;

        if (mustRecreateDropin.current) {
            mustRecreateDropin.current = false;
            // Important note: this part is only computed after the first render or when we increment the attempts count (ie after a payment error)
            // so if any component prop changes (interior mutability excluded) it will NOT be reflected in the dropin config until a payment error occurs
            // (simplest case: `hidePayButton` changes, more complex case: `onPaymentResult` changes so we call an old callback)
            const checkout = new AdyenCheckout({
                ...config.dropin_config,
                showPayButton: !hidePayButton,
                showStoredPaymentMethods: savePaymentMethod,
                onSubmit: processAdyen('create', token, config, lyriaUrl, iframe, popup, handleSuccess, handleError, setCurrentView, onPaymentStarted),
                onAdditionalDetails: processAdyen('complete', token, config, lyriaUrl, iframe, popup, handleSuccess, handleError, setCurrentView, onPaymentStarted),
                onError(error) {
                    // client-side form validation error
                    // warn the host so that it re-enables the host pay button (if there is an host pay button)
                    // note: this is triggered *a lot* but if the host follows the integration doc correctly
                    // ie by not doing anything if !res.success except re-enabling the host pay button
                    // everything should be alright
                    onPaymentResult({success: false});
                },
            });
            const newDropin = checkout.create('dropin', {
                ...config.dropin_options, // note: dropin_options is not return by the lyria yet
            });

            dropin.current = newDropin;
            newDropin.mount(element);
        }

        return () => {
            if (mustRecreateDropin.current) {
                dropin.current.unmount(element);
            }
        };
    }, [attempts, config, hidePayButton, token, lyriaUrl, handleSuccess, handleError, onPaymentResult, onPaymentStarted, savePaymentMethod]);

    React.useImperativeHandle(ref, () => ({
        pay() {
            if (dropin.current) {
                dropin.current.submit();
                // dropin.submit is asynchronous so we don't know yet if the form is filled in correctly
                // so we return true as if it was filled in correctly
                // `AdyenCheckout` will call the `onError` callback if the form is _not_ filled in correctly
                // and in onError we'll warn the host
                return true;
            }
        }
    }));

    return (
        <div className="wz-lyriapay-adyen">
            <div
                ref={container}
                style={currentView === 'adyen' ? {
                    display: 'block',
                } : {
                    display: 'none'
                }}
            />
            { currentView === 'popup' && (
                <WaitingBlock loader={loader}>
                    <Trans i18nKey="adyenPopupProcess" />
                </WaitingBlock>
            ) }
            <iframe
                className="wz-lyriapay-iframe wz-lyriapay-adyen-iframe"
                title="iframe"
                ref={iframe}
                key={`iframe${attempts}` /* recreate a new iframe for each time we try the payment */ }
                style={
                    currentView === 'iframe' ? {} : {display: 'none'}
                }
            />
        </div>
    );
});

function processAdyen(processType, token, config, lyriaUrl, iframe, popup, handleSuccess, handleError, setCurrentView, onPaymentStarted) {
    return (state, dropin) => {
        onPaymentStarted();
        // note: if processType is "complete" (aka onAdditionalDetails there is not paymentMethod field in state.data)
        // note state.data.paymentMethod.type is "scheme" if payment by card, need to check the "brand" field
        // TODO set a redirect mode for a scheme paymenet method in the backend to check what's the result in /paymentConfig
        const redirectMode = 'paymentMethod' in state.data && config.redirect_modes && config.redirect_modes[state.data.paymentMethod.type];
        if (redirectMode === 'popup') {
            // open popup in the onclick handler
            // because it will be blocked by the browsers if we open it in the /adyen/process request callback
            popup.current = window.open('', 'PaymentPopup', 'dependent'); // TODO open a nice loading page instead of about:blank
            if (popup.current !== null) {
                setCurrentView('popup');
            }
        }
        axios({
            method: 'post',
            url: lyriaUrl + '/adyen/process',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            data: {
                request_token: token,
                data: {adyen_data: state.data, action: processType, origin: window.location.href},
                psp: config.id
            }
        }).then(({ data }) => {
            if (popup.current) {
                popup.current.close();
                popup.current = null;
            }

            if (data.status === 'V' || data.statuts === 'P') {
                handleSuccess();
            } else if (data.status === 'A') {
                handleError(data.data);
            }
        }).catch((error) => {
            const action = error?.response?.data?.data?.action;
            if (action) {
                if (action.type === 'redirect' && action.method === 'GET') {
                    if (redirectMode === 'popup') {
                        if (popup.current !== null) {
                            setCurrentView('popup');  // not needed because currentView was already set before the request, but call anyway so that each branch has a "setCurrentView" call
                            window.open(action.url, 'PaymentPopup', 'dependent');
                        } else {
                            // if we get here (redirectMode === 'popup' && popup.current === null)
                            // it means that we can't open this payment's method in a iframe
                            // *and* that we couldn't open a popup
                            // so do a full page redirect instead...
                            setCurrentView('adyen');
                            dropin.handleAction(action);
                        }
                    } else if (redirectMode === 'host') {
                        setCurrentView('adyen');
                        dropin.handleAction(action);
                    } else {
                        iframe.current.setAttribute('src', action.url);
                        setCurrentView('iframe');
                    }
                } else if (action.type === 'redirect' && action.method === 'POST') {
                    if (popup.current) {
                        // we don't handle popup mode for post forms yet (ever ?)
                        // but in case a payment method is misconfigured and asks us to open a popup
                        // close it
                        popup.current.close();
                        popup.current = null;
                    }

                    const iframeContent = iframe.current.contentDocument;
                    const form = iframeContent.createElement('form');
                    form.setAttribute('method', action.method);
                    form.setAttribute('action', action.url);
                    for (let key in action.data) {
                        const hiddenField = iframeContent.createElement('input');
                        hiddenField.setAttribute('type', 'hidden');
                        hiddenField.setAttribute('name', key);
                        hiddenField.setAttribute('value', action.data[key]);
                        form.appendChild(hiddenField);
                    }
                    iframeContent.documentElement.appendChild(form);
                    form.submit();
                    setCurrentView('iframe');
                } else {
                    setCurrentView('adyen');
                    dropin.handleAction(action);
                }
            }
        });
    };
}

Adyen.propTypes = {
    token: PropTypes.string.isRequired,
    lyriaUrl: PropTypes.string.isRequired,
    sandbox: PropTypes.bool.isRequired,
    config: PropTypes.object,
    expiryAsText: PropTypes.bool,
    onPaymentStarted: PropTypes.func.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    hidePayButton: PropTypes.bool,
    loader: PropTypes.node,
    savePaymentMethod: PropTypes.bool
};

export default Adyen;
